/**
 * Shipment Data Adapter
 *
 * Adapter for generating Excel files from shipment/packing list data.
 * Implements the global excel configurator interface.
 */

import {
  BaseDataSourceAdapter,
  type FieldMetadata,
  type ExcelConfiguration,
} from "@/lib/excel-configurator/core";
import type { Shipment } from "@/types";
import { buildDefaultShipmentConfiguration } from "./shipment-default-configuration";
import { buildShipmentFields } from "./shipment-fields";
import type {
  EnrichedShipment,
  ShipmentEnrichment,
  ShipmentRowData,
} from "./shipment-row-data";

export type { EnrichedShipment, ShipmentEnrichment, ShipmentRowData };

export class ShipmentDataAdapter extends BaseDataSourceAdapter<Shipment, ShipmentRowData> {
  readonly type = "shipment";
  readonly name = "Shipment / Packing List";
  readonly description = "Generate Excel from shipment packing list data with variants and quantities";

  /**
   * Enrich a shipment with order and client data for Excel generation
   */
  static enrich(
    shipment: Shipment,
    enrichment: ShipmentEnrichment
  ): EnrichedShipment {
    return { ...shipment, _enrichment: enrichment };
  }

  /**
   * Get available fields from shipment data
   * Returns 40+ fields organized by category
   */
  getAvailableFields(): FieldMetadata[] {
    return buildShipmentFields();
  }

  /**
   * Extract rows from shipment entity (supports enriched shipments)
   */
  extractRows(source: Shipment): ShipmentRowData[] {
    const rows: ShipmentRowData[] = [];
    const enriched = source as EnrichedShipment;
    const order = enriched._enrichment?.order;
    const client = enriched._enrichment?.client;

    // Iterate through all packing lists
    for (const packingList of source.packing_lists || []) {
      // Iterate through all items in the packing list
      for (const item of packingList.items || []) {
        rows.push({
          // Article fields
          article_name: item.article_name,
          article_code: item.article_code,
          client_reference: item.client_reference,
          client_description: item.client_description,

          // Variant fields
          colour: item.colour,
          size: item.size,

          // Barcode fields
          barcode_type: item.barcode_type,
          barcode_value: item.barcode_value,

          // Quantity fields
          quantity: item.quantity,

          // Box/Logistics fields
          box_number: item.box_number,
          weight: item.weight,
          volume: item.volume,
          lot_number: item.lot_number,

          // Packing list fields
          packing_list_code: packingList.code,
          packing_list_total_boxes: packingList.total_boxes,
          packing_list_net_weight: packingList.net_weight,
          packing_list_gross_weight: packingList.gross_weight,

          // Shipment fields
          shipment_code: source.code,
          shipment_date: source.shipment_date || null,
          shipment_tracking_number: source.tracking_number || null,
          shipment_carrier: source.carrier || null,
          shipment_status: source.status,

          // Order fields (populated from enrichment)
          order_code: order?.code ?? null,
          order_date: order?.created_at ?? null,
          order_status: order?.status ?? null,
          order_priority: null,
          purchase_order_number: order?.purchase_order_number ?? null,
          order_expected_ship_date: order?.expected_ship_date ?? null,

          // Client fields (populated from enrichment)
          client_name: client?.socialDesignation ?? null,
          client_code: client?.code ?? null,
          client_email: client?.primaryContactEmail ?? null,
          client_phone: client?.primaryContactPhone ?? null,
          client_tax_id: client?.taxNumber ?? null,

          // Delivery address fields
          delivery_address_line1: source.delivery_address?.address_line1 || null,
          delivery_city: source.delivery_address?.city || null,
          delivery_postal_code: source.delivery_address?.postal_code || null,
          delivery_country: source.delivery_address?.country_id || null,
        });
      }
    }

    return rows;
  }

  /**
   * Get default configuration for shipments
   */
  getDefaultConfiguration(): ExcelConfiguration<Shipment> {
    return buildDefaultShipmentConfiguration(this.type);
  }
}


// ============================================================================
// Singleton Instance
// ============================================================================

/**
 * Singleton instance of ShipmentDataAdapter
 */
export const shipmentDataAdapter = new ShipmentDataAdapter();
